<template>
  <div :class="classes" :style>
    <div class="v-progress-indeterminate__bar" :style="barStyle" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { VLoadingBarPlacement } from '../types.ts'

interface Props {
  size?: number
  placement?: VLoadingBarPlacement
  loaderColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 3,
  placement: '',
  loaderColor: undefined,
})

const classes = computed(() => ({
  'v-progress-indeterminate': true,
  [`is-${props.placement}`]: props.placement,
}))

const style = computed(() => ({
  height: `${props.size}px`,
  marginBottom: !props.placement ? `-${props.size}px` : '0px',
}))

const barStyle = computed(() => ({
  backgroundColor: props.loaderColor,
}))
</script>

<style>
.v-progress-indeterminate {
  position: relative;
  width: 100%;
  overflow-x: hidden;
  z-index: 1;
  background-color: var(--color-blue-4);
  border-radius: var(--border-radius);
  &.is-bottom {
    position: absolute !important;
    left: 0;
    bottom: 0;
  }

  &.is-top {
    position: absolute !important;
    left: 0;
    top: 0;
  }

  .v-progress-indeterminate__bar {
    background-color: var(--color-primary);
    border-radius: var(--border-radius);

    &:before {
      content: '';
      position: absolute;
      background-color: inherit;
      border-radius: var(--border-radius);
      width: 100%;
      height: 100%;
      left: 0;
      transform-origin: left;
      animation: indeterminate 2.4s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;
      animation-delay: -1.2s;
    }

    &:after {
      content: '';
      position: absolute;
      background-color: inherit;
      border-radius: var(--border-radius);
      width: 100%;
      height: 100%;
      left: 0;
      transform-origin: left;
      animation: indeterminate-short 2.4s cubic-bezier(0.165, 0.84, 0.44, 1) infinite;
      animation-delay: 0;
    }
  }
}

@keyframes indeterminate {
  0% {
    transform: translateX(-37%) scaleX(0.35);
  }
  60% {
    transform: translateX(100%) scaleX(0.9);
  }
  100% {
    transform: translateX(100%) scaleX(0.9);
  }
}

@keyframes indeterminate-short {
  0% {
    transform: translateX(-202%) scaleX(2);
  }
  60% {
    transform: translateX(105%) scaleX(0.01);
  }
  100% {
    transform: translateX(105%) scaleX(0.01);
  }
}
</style>
